/**
 * Analytics Service - Anonymous usage tracking
 * 
 * Tracks user interactions to help understand app usage patterns
 * No personally identifiable information is collected
 */

// Use relative URL in production (same server), localhost:3001 in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// Generate or retrieve session ID from localStorage (persists across browser sessions)
// This ensures we track unique users, not just browser sessions
function getSessionId(): string {
  let sessionId = localStorage.getItem('astropower-session-id');
  
  if (!sessionId) {
    sessionId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('astropower-session-id', sessionId);
  }
  
  return sessionId;
}

export interface AnalyticsEvent {
  eventType: string;
  eventData?: Record<string, any>;
}

/**
 * Track an analytics event
 * Events are sent to backend asynchronously and failures are silently ignored
 */
export async function trackEvent(event: AnalyticsEvent): Promise<void> {
  try {
    const sessionId = getSessionId();
    
    await fetch(`${API_BASE_URL}/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventType: event.eventType,
        eventData: event.eventData,
        sessionId
      })
    });
  } catch (error) {
    // Silently fail - analytics should never break the app
    console.debug('Analytics tracking failed:', error);
  }
}

/**
 * Common event types for consistent tracking
 */
export const AnalyticsEvents = {
  // App lifecycle
  APP_LOADED: 'app_loaded',
  
  // Rig management
  RIG_CREATED: 'rig_created',
  RIG_RENAMED: 'rig_renamed',
  RIG_DELETED: 'rig_deleted',
  RIG_SWITCHED: 'rig_switched',
  RIG_IMPORTED: 'rig_imported',
  RIG_EXPORTED: 'rig_exported',
  
  // Device management
  DEVICE_ADDED: 'device_added',
  DEVICE_EDITED: 'device_edited',
  DEVICE_DELETED: 'device_deleted',
  DEVICE_FROM_TEMPLATE: 'device_from_template',
  DEVICE_MANUAL: 'device_manual',
  
  // Template usage
  TEMPLATE_SEARCHED: 'template_searched',
  TEMPLATE_FILTERED: 'template_filtered',
  TEMPLATE_SUBMITTED: 'template_submitted',
  
  // Admin actions
  ADMIN_LOGIN: 'admin_login',
  TEMPLATE_APPROVED: 'template_approved',
  TEMPLATE_REJECTED: 'template_rejected',
  TEMPLATE_IMPORTED_BULK: 'template_imported_bulk',
  TEMPLATE_EXPORTED_BULK: 'template_exported_bulk',
} as const;
